/* eslint-disable max-lines-per-function */
import React from 'react';
import Image from 'next/image';
import {
  selectRemoveAllProductsCart,
  useCartStore,
  type CartItem,
  type CartItemProduct,
} from '@/store/use-cart.store';
import { selectIsTenant, useDomainStore } from '@/store/use-domain.store';
import { useRouter } from 'nextjs-toploader/app';
import { isDesktop } from 'react-device-detect';
import type { BasketCheckAvailabilityItem } from '@/common/apis/cart/basket-check-availability';
import AnalyticsWrapper from '@/common/components/analytics-wrapper';
import Button from '@/common/components/button';
import CardDivider from '@/common/components/card-divider';
import DiagonalLine from '@/common/components/diagonal-line/diagonal-line.component';
import CommonLightCard from '@/common/components/light-card';
import OrderProduct from '@/common/components/order-product';
import SkeletonBuilder from '@/common/components/skeleton-builder';
import Text from '@/common/components/text';
import Wrapper from '@/common/components/wrapper';
import { appConfigs } from '@/common/configs/app.config';
import { useBasket } from '@/common/hooks/use-basket.hook';
import { rialToToman } from '@/common/utils/money-exchange.utility';
import Archive from '~/public/assets/images/svg/archive-stack-line.svg';
import PriceTag from '~/public/assets/images/svg/price-tag-3-line.svg';
import CartOpertaion from './cart-operations.component';

interface CartItemProps {
  item?: CartItem | null;
  remoteItems?: BasketCheckAvailabilityItem[];
  count?: number;
}

export default function Item({ item, remoteItems, count = 1 }: CartItemProps) {
  const router = useRouter();
  const { removeBasket } = useBasket(item?.vendor.id || 0);
  const removeAll = useCartStore(selectRemoveAllProductsCart);
  const isTenant = useDomainStore(selectIsTenant);

  const onRemoveAllClickHandler = () => {
    if (item) {
      removeAll(item.vendor.id);
      removeBasket(item.vendor.id);
    }
  };

  const handleProductLink = (slug: string, productName: string, id: number) => {
    isTenant
      ? router.push(`/product/${id}/${productName}`)
      : router.push(`/${slug}/${id}`);
  };

  return (
    <Wrapper className="!h-full  px-4 lg:min-h-[300px] lg:border-l lg:border-[#EDECEB] lg:!p-[24px]">
      {item ? (
        <CommonLightCard className="!h-fit">
          <CommonLightCard.Body className="w-full !flex-row items-center justify-between lg:!mt-0">
            {/* <CommonLightCard.Item
              className="cursor-pointer"
              onClick={() => {
                router.push(`/${item.vendor.slug}`);
              }}
            > */}
            {!isTenant ? (
              <>
                <CommonLightCard.Item
                  className="cursor-pointer"
                  onClick={() => {
                    router.push(`/${item.vendor.slug}`);
                  }}
                >
                  <CommonLightCard.Icon className="overflow-hidden border !border-beer-20 ">
                    <Image
                      src={
                        item?.vendor.vendorMedia?.vendorLogo?.url ??
                        '/assets/images/png/sample-logo.png'
                      }
                      width={48}
                      height={48}
                      alt={
                        `لوگوی ${item?.vendor.vendorName}` || 'لوگوی فروشگاه'
                      }
                    />
                  </CommonLightCard.Icon>
                  <CommonLightCard.Title className="!text-sm !font-semibold lg:!text-base lg:!text-[#174170] ">
                    {item.vendor.vendorName}
                  </CommonLightCard.Title>
                </CommonLightCard.Item>
              </>
            ) : (
              <>
                <CommonLightCard.Item>
                  <CommonLightCard.Title className="!text-sm !font-semibold lg:!text-base lg:!text-[#174170] lg:pr-2">
                    لیست محصولات
                  </CommonLightCard.Title>
                </CommonLightCard.Item>
              </>
            )}
            {/* </CommonLightCard.Item> */}
            <CommonLightCard.Item>
              <AnalyticsWrapper analytics={['checkout-click-clear-cart-v1']}>
                <Button
                  id="checkout-click-clear-cart-v1"
                  className="!h-10 w-20 !px-1"
                  type="outline"
                  onClick={onRemoveAllClickHandler}
                >
                  حذف همه
                </Button>
              </AnalyticsWrapper>
            </CommonLightCard.Item>
          </CommonLightCard.Body>
        </CommonLightCard>
      ) : (
        <SkeletonBuilder type="light-card" />
      )}

      <div className="mt-2 flex items-center justify-between py-1.5 lg:hidden">
        <Text className="text-sm font-semibold text-secondary-300">
          تعداد محصولات
        </Text>
        <Text className="text-sm font-medium text-secondary-300">
          {item?.products.length} محصول
        </Text>
      </div>
      {remoteItems && item ? (
        remoteItems.map(
          (
            {
              variants,
              picUrl,
              productName,
              varaintId,
              productVariantPrice,
              varinatDiscount,
              productVariantAvailability,
            },
            index
          ) => {
            const variantItem = variants
              .map((item) => item.variantValueTitle)
              .join(' - ');
            const productItem = item.products.find(
              (pItem) => pItem.productVariantId === varaintId
            ) as CartItemProduct;
            return (
              <div
                className="flex flex-col justify-evenly lg:mt-3"
                key={varaintId}
              >
                {isDesktop ? (
                  <OrderProduct
                    className="my-3 w-full "
                    disable={!productVariantAvailability}
                  >
                    <div className="relative h-20 w-20 self-start justify-self-start overflow-hidden lg:h-auto  lg:w-auto  ">
                      <OrderProduct.ProductImage
                        src={picUrl}
                        alt={productName}
                        className="self-start justify-self-start"
                        onClick={() => {
                          handleProductLink(
                            item.vendor.slug,
                            productName,
                            productItem?.productId
                          );
                        }}
                      />
                      {!productVariantAvailability && (
                        <DiagonalLine className="!absolute !bottom-[1px] !left-[1px]  h-[97%] w-[98%]" />
                      )}
                    </div>

                    <OrderProduct.Container
                      type="col"
                      className="flex-grow lg:h-auto"
                    >
                      <OrderProduct.Container
                        type="row"
                        className="items-start lg:h-auto "
                      >
                        <OrderProduct.Container
                          type="col"
                          className="mb-3"
                          onClick={() => {
                            handleProductLink(
                              item.vendor.slug,
                              productName,
                              productItem?.productId
                            );
                          }}
                        >
                          <OrderProduct.Title
                            className="-mb-0.5 h-auto lg:mb-2 "
                            onClick={() => {
                              handleProductLink(
                                item.vendor.slug,
                                productName,
                                productItem?.productId
                              );
                            }}
                          >
                            {productName}
                          </OrderProduct.Title>

                          <OrderProduct.Properties>
                            <div className="mb-0.5 flex items-center justify-center ">
                              <Archive />
                            </div>

                            <Text className=" text-ellipsis whitespace-nowrap text-sm font-medium text-secondary-400 ">
                              {productItem?.quantity} عدد
                            </Text>
                          </OrderProduct.Properties>

                          <OrderProduct.Properties>
                            <div className=" mb-0.5 flex items-center justify-center ">
                              <PriceTag />
                            </div>

                            <Text className=" text-ellipsis whitespace-nowrap text-sm font-medium text-secondary-400 ">
                              {variantItem.length
                                ? variantItem
                                : '\u00A0 ---- \u00A0 '}
                            </Text>
                          </OrderProduct.Properties>
                        </OrderProduct.Container>
                        <OrderProduct.Container type="column">
                          <OrderProduct.ProductPrice
                            priceClassName="text-black-default text-sm font-semibold"
                            discountClassName="text-secondary-300 !text-sm !font-normal leading-[18px]"
                            price={rialToToman(
                              (productVariantPrice
                                ? Number(productVariantPrice) *
                                  productItem?.quantity
                                : 0) || 0,
                              appConfigs.rialExchangeRate
                            )}
                            discount={Math.round(varinatDiscount * 100)}
                          />
                          <OrderProduct.Container
                            type="row"
                            className="lg:mt-2 lg:h-auto"
                          >
                            <CartOpertaion
                              disable={!productVariantAvailability}
                              item={item}
                              productId={productItem?.productId}
                              productVariantId={varaintId}
                              quantity={productItem?.quantity}
                            />
                          </OrderProduct.Container>
                        </OrderProduct.Container>
                      </OrderProduct.Container>
                    </OrderProduct.Container>
                  </OrderProduct>
                ) : (
                  <OrderProduct
                    className="my-3 w-full "
                    disable={!productVariantAvailability}
                  >
                    <div className="relative h-20 w-20 self-start justify-self-start overflow-hidden lg:h-[105px]  lg:w-40  ">
                      <OrderProduct.ProductImage
                        src={picUrl}
                        alt={productName}
                        className="self-start  justify-self-start "
                        onClick={() => {
                          handleProductLink(
                            item.vendor.slug,
                            productName,
                            productItem?.productId
                          );
                        }}
                      />
                      {!productVariantAvailability && (
                        <DiagonalLine className="!absolute !bottom-[1px] !left-[1px]  h-[97%] w-[98%]" />
                      )}
                    </div>

                    <OrderProduct.Container type="col" className="flex-grow">
                      <OrderProduct.Container
                        type="row"
                        className=" items-start"
                      >
                        <OrderProduct.Container
                          type="col"
                          onClick={() => {
                            handleProductLink(
                              item.vendor.slug,
                              productName,
                              productItem?.productId
                            );
                          }}
                        >
                          <OrderProduct.Title
                            className="-mb-0.5 h-auto lg:mb-2 "
                            onClick={() => {
                              handleProductLink(
                                item.vendor.slug,
                                productName,
                                productItem?.productId
                              );
                            }}
                          >
                            {productName}
                          </OrderProduct.Title>

                          <OrderProduct.Properties>
                            <div className="mb-0.5 flex items-center justify-center ">
                              <Archive />
                            </div>

                            <Text className=" text-ellipsis whitespace-nowrap text-sm font-medium text-secondary-400 ">
                              {productItem?.quantity} عدد
                            </Text>
                          </OrderProduct.Properties>

                          <OrderProduct.Properties>
                            <div className=" mb-0.5 flex items-center justify-center ">
                              <PriceTag />
                            </div>

                            <Text className=" text-ellipsis whitespace-nowrap text-sm font-medium text-secondary-400 ">
                              {variantItem.length
                                ? variantItem
                                : '\u00A0 ---- \u00A0 '}
                            </Text>
                          </OrderProduct.Properties>
                        </OrderProduct.Container>
                        <OrderProduct.Container type="column">
                          <OrderProduct.ProductPrice
                            priceClassName="text-black-default text-sm font-semibold"
                            discountClassName="text-secondary-300 !text-sm !font-normal leading-[18px]"
                            price={rialToToman(
                              (productVariantPrice
                                ? Number(productVariantPrice) *
                                  productItem?.quantity
                                : 0) || 0,
                              appConfigs.rialExchangeRate
                            )}
                            discount={Math.round(varinatDiscount * 100)}
                          />
                        </OrderProduct.Container>
                      </OrderProduct.Container>
                      <CartOpertaion
                        disable={!productVariantAvailability}
                        item={item}
                        productId={productItem?.productId}
                        productVariantId={varaintId}
                        quantity={productItem?.quantity}
                      />
                    </OrderProduct.Container>
                  </OrderProduct>
                )}

                {item.products.length > 1 &&
                  index !== item.products.length - 1 && (
                    <CardDivider itemClassName="!px-0 " />
                  )}
              </div>
            );
          }
        )
      ) : (
        <SkeletonBuilder type="order-product" repeat={count} />
      )}
    </Wrapper>
  );
}
